import { gsap } from "gsap";
import { IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { GameFont } from "../../../../common/GameFont";
import { ButtonWithScaleAnimation } from "../../../../common/components/ButtonWithScaleAnimation";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { LoginResourcePackage } from "../../resources/LoginResourcePackage";
import { InputType } from "../../InputType";
import { LoginHtmlInput } from "by/gamefactory/cityversetycoon/scenes/common/components/LoginHtmlInput";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { TextWithUnderline } from "by/gamefactory/cityversetycoon/scenes/game/components/TextWithUnderline";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { IGuestModel } from "../../../../common/models/IGuestModel";
import { AsyncOperationNotification } from "../../../../common/components/AsyncOperationNotification";
import { IFocusable } from "by/gamefactory/cityversetycoon/scenes/layers/IFocusable";
import { StringFormatExtention } from "../../../../../../extensions/StringFormatExtention";
import { VirtualScreenEvent } from "../../../../../../analytics/events/VirtualScreenEvent";
import { BaseScalableView } from "by/gamefactory/cityversetycoon/scenes/common/BaseScalableView";
import { IEmailAuthPopupController } from "../controller/IEmailAuthPopupController";
import { FullscreenInputBlocker } from "by/gamefactory/cityversetycoon/scenes/game/components/FullscreenInputBlocker";

export class EmailAuthPopupView extends BaseScalableView<boolean> implements IFocusable {
    public readonly focusable: boolean = true;

    private static readonly HEADER_TEXT = "ENTER YOUR EMAIL ADDRESS";
    private static readonly DESCRIPTION_TEXT = "A verification code will be sent\nto the email address you provide.";
    private static readonly PLEASE_CHECK_TEXT = "Please check your spam folder";
    private static readonly CONTINUE_BUTTON_TEXT = "CONTINUE";
    private static readonly CONTACT_US_TEXT = "CONTACT US";
    private static readonly FOOTER_TEXT = "WE'LL SET UP AN IN-GAME WALLET\nFOR YOU USING YOUR EMAIL ADDRESS";

    private readonly loginResources: LoginResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly emailAuthController: IEmailAuthPopupController;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly guestModel: IGuestModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private fadeScreen: FadeScreen;
    private backButton: ButtonWithScaleAnimation;
    private emailIcon: Sprite;
    private headerText: Text;
    private descriptionText: Text;
    private descriptionCheckFolderText: Text;
    private continueButton: ButtonWithScaleAnimation;
    private resetButtonText: Text;
    private contactUsButton: TextWithUnderline;
    private emailInput: LoginHtmlInput;
    private footerText: Text;

    public constructor(
        operationControls: PromiseCompletionSource<boolean>,
        loginResources: LoginResourcePackage,
        commonResources: CommonResourcesPackage,
        canvasScaler: ICanvasScaler,
        emailAuthController: IEmailAuthPopupController,
        analyticsProvider: IAnalyticProviderWrapper,
        guestModel: IGuestModel,
        asyncOperationNotification: AsyncOperationNotification
    ) {
        super(operationControls, canvasScaler);
        this.loginResources = loginResources;
        this.commonResources = commonResources;
        this.emailAuthController = emailAuthController;
        this.analyticsProvider = analyticsProvider;
        this.guestModel = guestModel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public start(): void {
        this.show();
    }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);

        this.backButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_back_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height, this.backButton.width * 2, this.backButton.height * 2);
        this.backButton.onUp.add(this.backButtonClickHandle, this);
        this.addChild(this.backButton);

        this.emailIcon = new Sprite(this.loginResources.assets_views_login_login_failed_icons_email_icon_png);
        this.addChild(this.emailIcon);

        this.headerText = new Text(EmailAuthPopupView.HEADER_TEXT, new HeaderTextStyle());
        this.addChild(this.headerText);

        this.descriptionText = new Text(EmailAuthPopupView.DESCRIPTION_TEXT, new DescriptionTextStyle());
        this.addChild(this.descriptionText);

        this.descriptionCheckFolderText = new Text(EmailAuthPopupView.PLEASE_CHECK_TEXT, new AnotherDescriptionTextStyle());
        this.addChild(this.descriptionCheckFolderText);

        this.emailInput = new LoginHtmlInput(this.loginResources, this.canvasScaler, InputType.EMAIL, this.guestModel.email.value != undefined && this.guestModel.email.value != "");
        this.addChild(this.emailInput);

        this.continueButton = new ButtonWithScaleAnimation(this.loginResources.assets_views_login_buttons_button_green_long_png);
        this.continueButton.onUp.add(this.continueButtonClickHandle, this);
        this.resetButtonText = new Text(EmailAuthPopupView.CONTINUE_BUTTON_TEXT, new ButtonTextStyle());
        this.continueButton.defaultView.addChild(this.resetButtonText);
        this.addChild(this.continueButton);

        this.contactUsButton = new TextWithUnderline(EmailAuthPopupView.CONTACT_US_TEXT, new ContactUsTextStyle(), 0.5, -4, 2);
        this.contactUsButton.anchor.set(0.5, 0);
        this.contactUsButton.eventMode = 'static';
        this.contactUsButton.on('pointerup', this.contactUsClickHandle, this);
        this.addChild(this.contactUsButton);

        this.footerText = new Text(EmailAuthPopupView.FOOTER_TEXT, new FooterTextStyle());
        this.addChild(this.footerText);
    }

    private async show(): Promise<void> {
        if (this.guestModel.email.value != undefined && this.guestModel.email.value != "") {
            this.emailInput.value = StringFormatExtention.emailFormatWithStars(this.guestModel.email.value);
        }
        this.fadeScreen.fadeIn();
        this.emailInput.hide();
        const showAnimation = new gsap.core.Timeline();
        showAnimation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.15, ease: "power1.in" });
        await showAnimation.play();
        showAnimation.kill();
        this.emailInput.show();
    }

    private async backButtonClickHandle(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        this.emailInput.hide();
        await this.hideView();
        this.operationControls.complete(false);
    }

    private async contactUsClickHandle(): Promise<void> {
        this.emailAuthController.openIntercom();
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Email contact us button click", this.analyticsProvider.currentEventIndex));
    }

    private async continueButtonClickHandle(): Promise<void> {
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Email continue button click", this.analyticsProvider.currentEventIndex));
        if (!this.emailInput.isTextMatchingRegex()) {
            return;
        }

        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        this.emailInput.hide();
        await this.asyncOperationNotification.autoShowHide(this.emailAuthController.signWithEmail(this.emailInput.value));
        await this.hideView();

        if (await this.emailAuthController.showEmailVerificationPopup(this.emailInput.value)) {
            this.operationControls.complete(true);
            return;
        }

        await this.show();
        this.emailInput.show();
        this.removeChild(inputBlocker);
    }

    private async hideView(): Promise<void> {
        this.emailInput.hide();
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.15, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
    }

    private layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.backButton.position.set(56, 235);
        this.emailIcon.position.set(300, 513);
        this.headerText.position.set(209, 695);
        this.descriptionText.position.set(185, 785);
        this.descriptionCheckFolderText.position.set(204, 868);
        this.emailInput.position.set(170, 936);
        this.continueButton.position.set(415 - this.continueButton.width * 0.5, 1160 - this.continueButton.height * 0.5);
        this.resetButtonText.position.set((this.continueButton.width - this.resetButtonText.width) * 0.5, (this.continueButton.height - this.resetButtonText.height) * 0.5);
        this.contactUsButton.position.set(415, 1250);
        this.footerText.position.set(201, height - this.footerText.height - 96);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.backButton.onUp.remove(this.backButtonClickHandle, this);
        this.continueButton.onUp.remove(this.continueButtonClickHandle, this);
        this.contactUsButton.off('pointerup', this.contactUsClickHandle, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48,
            lineHeight: 52.5
        }));
    }
}

class DescriptionTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT_REGULAR,
            fontSize: 40,
            lineHeight: 40,
            letterSpacing: 1.6
        }));
    }
}

class AnotherDescriptionTextStyle extends DescriptionTextStyle {
    public constructor() {
        super();
        this.fill = '#BBFF65';
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.8,
            dropShadowBlur: 5,
            dropShadowDistance: 2,
            dropShadowAlpha: 0.5,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64,
            miterLimit: 0
        }));
    }
}

class ContactUsTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40,
        }));
    }
}

class FooterTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ADADAD",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36,
            lineHeight: 42
        });
    }
}